import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ModalComponent } from '@app/partials/modal/modal.component';
import { EventGuestModel } from '@app/events/shared/event-guest.model';
import { EventAccommodationService } from '@app/events/shared/event-accommodation.service';
import { ToastService } from '@app/shared/toast/toast.service';

export type AccommodationModalMode = 'add' | 'edit';

export interface AccommodationModalContext {
  mode: AccommodationModalMode;
  // The room the guest will be placed in. Null when opening from the
  // unallocated panel (the user must pick a room in the modal).
  roomNumber: string | null;
  roomLabel: string | null;
  // Add mode: the unallocated guests dropdown. Edit mode: empty.
  candidates: EventGuestModel[];
  // Pre-selected guest. In edit mode: the existing allocation. In add mode:
  // optional pre-selection (e.g. when launched from the unallocated panel).
  guest: EventGuestModel | null;
}

@Component({
  selector: 'app-accommodation-modal',
  standalone: true,
  imports: [CommonModule, ModalComponent, ReactiveFormsModule],
  templateUrl: './accommodation-modal.component.html',
})
export class AccommodationModalComponent implements OnChanges {
  @Input({ required: true }) show = false;
  @Input({ required: true }) context!: AccommodationModalContext;
  @Input({ required: true }) eventId!: number;
  @Input() customerPortal = false;

  @Output() modalClose = new EventEmitter<void>();
  @Output() allocationSaved = new EventEmitter<void>();

  protected processing = false;
  protected selectedGuest: EventGuestModel | null = null;

  protected form = new FormGroup({
    guest_id: new FormControl<number | null>(null, Validators.required),
    allocated_room_number: new FormControl<string | null>(null, Validators.required),
    mobile: new FormControl<string>(''),
    accommodation_emergency_contact_name: new FormControl<string>(''),
    accommodation_emergency_contact_number: new FormControl<string>(''),
    accommodation_role: new FormControl<string>(''),
    accommodation_breakfast: new FormControl<boolean>(true),
  });

  constructor(
    private accommodationService: EventAccommodationService,
    private toast: ToastService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['show'] || changes['context']) && this.show && this.context) {
      this.resetFormFromContext();
    }
  }

  protected get title(): string {
    return this.context?.mode === 'edit' ? 'Edit Allocation' : 'Allocate Guest';
  }

  protected get isAddMode(): boolean {
    return this.context?.mode === 'add';
  }

  protected onGuestSelected(guestId: number | null): void {
    if (!guestId) {
      this.selectedGuest = null;
      return;
    }

    const candidate = this.context.candidates.find((g) => g.id === guestId) ?? null;
    this.selectedGuest = candidate;

    if (candidate) {
      this.form.patchValue(
        {
          guest_id: candidate.id,
          mobile: candidate.mobile ?? '',
          accommodation_emergency_contact_name: candidate.accommodation_emergency_contact_name ?? '',
          accommodation_emergency_contact_number: candidate.accommodation_emergency_contact_number ?? '',
          accommodation_role: candidate.accommodation_role ?? '',
          accommodation_breakfast: candidate.accommodation_breakfast ?? true,
        },
        { emitEvent: false },
      );
    }
  }

  protected save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const guestId = this.form.controls.guest_id.value;
    if (!guestId) {
      return;
    }

    this.processing = true;
    this.accommodationService
      .allocateGuest(
        this.eventId,
        guestId,
        {
          allocated_room_number: this.form.controls.allocated_room_number.value,
          mobile: this.form.controls.mobile.value || null,
          accommodation_emergency_contact_name:
            this.form.controls.accommodation_emergency_contact_name.value || null,
          accommodation_emergency_contact_number:
            this.form.controls.accommodation_emergency_contact_number.value || null,
          accommodation_role: this.form.controls.accommodation_role.value || null,
          accommodation_breakfast: !!this.form.controls.accommodation_breakfast.value,
        },
        this.customerPortal,
      )
      .subscribe({
        next: () => {
          this.processing = false;
          this.toast.success('Allocation saved');
          this.allocationSaved.emit();
        },
        error: () => {
          this.processing = false;
          this.toast.error('Unable to save allocation');
        },
      });
  }

  protected unassign(): void {
    if (this.context.mode !== 'edit' || !this.context.guest) {
      return;
    }

    this.processing = true;
    this.accommodationService
      .allocateGuest(
        this.eventId,
        this.context.guest.id,
        { allocated_room_number: null },
        this.customerPortal,
      )
      .subscribe({
        next: () => {
          this.processing = false;
          this.toast.success('Guest unassigned');
          this.allocationSaved.emit();
        },
        error: () => {
          this.processing = false;
          this.toast.error('Unable to unassign guest');
        },
      });
  }

  protected close(): void {
    if (this.processing) {
      return;
    }
    this.modalClose.emit();
  }

  private resetFormFromContext(): void {
    const guest = this.context.guest;

    this.form.reset(
      {
        guest_id: guest?.id ?? null,
        allocated_room_number: this.context.roomNumber ?? guest?.allocated_room_number ?? null,
        mobile: guest?.mobile ?? '',
        accommodation_emergency_contact_name: guest?.accommodation_emergency_contact_name ?? '',
        accommodation_emergency_contact_number: guest?.accommodation_emergency_contact_number ?? '',
        accommodation_role: guest?.accommodation_role ?? '',
        accommodation_breakfast: guest?.accommodation_breakfast ?? true,
      },
      { emitEvent: false },
    );

    this.selectedGuest = guest;

    // In edit mode the room is fixed (already allocated). In add mode users
    // choose a guest first; if they were launched from a room card the room is
    // pre-set.
    if (this.context.mode === 'edit') {
      this.form.controls.guest_id.disable({ emitEvent: false });
    } else {
      this.form.controls.guest_id.enable({ emitEvent: false });
    }
  }
}
